import { CSSObject, Stack, Typography, styled } from '@mui/material';
import { motion } from 'framer-motion';

import { NavLink } from '@rumblefish/ui';
import { Section } from '@rumblefish/ui/Rumblefish23Theme';

export const WhoWeAreSection = styled(Section)(({ theme }) => ({
  margin: theme.spacing(16, 0, 16, 0),
  [theme.breakpoints.down('md')]: {
    margin: theme.spacing(15, 0, 15, 0),
  },
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(9, 0, 9, 0),
  },
}));

export const StyledContent = styled(Stack)(() => ({
  width: '100%',
  alignItems: 'flex-start',
}));

export const StyledHeader = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

export const StyledHeading = styled(Typography)(({ theme }) => ({
  ...(theme.mixins.neutralGradient as CSSObject),
  fontFamily: 'ClashDisplay',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 800,
  fontSize: `${theme.utils.fluidSize({ minSize: 34, maxSize: 48 })} !important`,
  lineHeight: 1.1,
}));

export const StyledAccentBar = styled('span')(({ theme }) => ({
  display: 'block',
  width: 56,
  height: 4,
  borderRadius: 2,
  background: theme.palette.cyanColors?._0,
}));

export const StyledIntro = styled(Typography)(({ theme }) => ({
  maxWidth: 760,
  color: theme.palette.invertedContent?.SECONDARY,
  fontSize: '18px',
  lineHeight: '30px',
}));

export const StyledTeamBannerLink = styled(NavLink)(({ theme }) => ({
  display: 'block',
  width: '100%',
  marginTop: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(4),
  },
}));

export const StyledTeamBanner = styled(motion.div)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(4),
  width: '100%',
  overflow: 'hidden',
  padding: theme.spacing(5, 6),
  borderRadius: theme.spacing(3),
  background: theme.palette.greyBackgroundColor?.INVERTED,
  border: `1px solid ${theme.palette.cyanColors?._0}59`,
  boxShadow: `0px 0px 80px 0px ${theme.palette.cyanColors?._0}1F, inset 0px 1px 0px 0px rgba(255, 255, 255, 0.06)`,
  transition: 'box-shadow 0.25s ease',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: `0px 0px 100px 0px ${theme.palette.cyanColors?._0}33, inset 0px 1px 0px 0px rgba(255, 255, 255, 0.06)`,
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.spacing(4),
    gap: theme.spacing(3),
  },
}));

export const StyledGlow = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '-30%',
  left: '30%',
  width: '55%',
  height: '160%',
  background: `radial-gradient(circle, ${theme.palette.cyanColors?._0}2E 0%, transparent 70%)`,
  filter: 'blur(80px)',
  pointerEvents: 'none',
  zIndex: 0,
}));

export const StyledAvatarStack = styled(Stack)(() => ({
  position: 'relative',
  zIndex: 1,
  flexDirection: 'row',
  flexShrink: 0,
}));

export const StyledAvatar = styled('img')(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  objectFit: 'cover',
  border: `2px solid ${theme.palette.greyBackgroundColor?.INVERTED}`,
  marginLeft: -14,
  '&:first-of-type': {
    marginLeft: 0,
  },
}));

export const StyledTeamTextGroup = styled(Stack)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  flex: '1 1 auto',
  gap: theme.spacing(0.5),
}));

export const StyledTeamHeading = styled(Typography)(({ theme }) => ({
  ...(theme.mixins.neutralGradient as CSSObject),
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
  fontSize: '20px',
}));

export const StyledTeamDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.invertedContent?.SECONDARY,
  fontSize: '14px',
  lineHeight: '20px',
}));

export const StyledTeamLink = styled('span')(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(4),
  border: `1px solid ${theme.palette.cyanColors?._0}`,
  background: `${theme.palette.cyanColors?._0}0F`,
  color: theme.palette.invertedContent?.PRIMARY,
  fontFamily: 'Satoshi',
  fontWeight: 650,
  fontSize: '14px',
  whiteSpace: 'nowrap',
  transition: 'background 0.25s ease, box-shadow 0.25s ease',
  '&:hover': {
    background: `${theme.palette.cyanColors?._0}24`,
    boxShadow: `0px 0px 24px 0px ${theme.palette.cyanColors?._0}59`,
  },
}));

export const StyledCountersWrapper = styled(motion.div)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(8),
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(6),
  },
}));
